import { Assets, Texture } from 'pixi.js';
import type { IAssets } from './IAssets';
import type { AssetManifest } from './AssetManifest';

/**
 * PixiAssets — IAssets adapter wrapping Pixi v8's Assets API + texture cache.
 *
 * Phase H10 Arc λ.4 (2026-05-09). Real impl that retires PhaserAssets
 * after Arc λ.7 default-flip. Mirrors PixiSprite.loadTexture's existing
 * pattern: `Assets.load({ src: dataUrl, alias: key })` with a local
 * Map<key, Texture> for synchronous `hasTexture(key)` lookups (Pixi v8's
 * Assets API doesn't expose a sync `has()` so we maintain our own).
 *
 * `addImage(key, htmlImage)` is the procedural-texture path used by
 * SpriteTextureSystem fallback generation. Pixi can build a Texture
 * from an HTMLImageElement directly, so we wrap and cache.
 */
export class PixiAssets implements IAssets {
  /** Sync texture cache. Mirrors PixiSprite's module-level cache shape. */
  private readonly textureCache = new Map<string, Texture>();
  private readonly progressListeners = new Set<(p: number) => void>();

  hasTexture(key: string): boolean {
    return this.textureCache.has(key);
  }

  async loadImage(key: string, urlOrDataUri: string): Promise<void> {
    if (this.textureCache.has(key)) return;
    // Pixi v8 dedupes internally on { src, alias }; subsequent calls with
    // the same alias return the same texture.
    const texture = await Assets.load<Texture>({ src: urlOrDataUri, alias: key });
    this.textureCache.set(key, texture);
  }

  addImage(key: string, image: HTMLImageElement): void {
    if (this.textureCache.has(key)) return;
    // Pixi v8 Texture.from accepts HTMLImageElement directly. We don't
    // route this through Assets.load because the source is already
    // decoded (no need for the loader pipeline).
    const texture = Texture.from(image);
    this.textureCache.set(key, texture);
  }

  async loadBatch(manifest: AssetManifest): Promise<void> {
    const total = manifest.length;
    if (total === 0) {
      this.notifyProgress(1);
      return;
    }
    let loaded = 0;
    await Promise.all(manifest.map(async (entry) => {
      await this.loadImage(entry.key, entry.dataUri);
      loaded += 1;
      this.notifyProgress(loaded / total);
    }));
  }

  onProgress(fn: (progress: number) => void): () => void {
    this.progressListeners.add(fn);
    return () => { this.progressListeners.delete(fn); };
  }

  /** Get the cached Pixi Texture for a key. PixiSprite.setTexture uses
   *  this to hand its inner Sprite a real Texture handle. */
  getTexture(key: string): Texture | undefined {
    return this.textureCache.get(key);
  }

  private notifyProgress(p: number): void {
    for (const fn of this.progressListeners) {
      try { fn(p); } catch { /* swallow listener errors */ }
    }
  }

  removeTexture(key: string): void {
    const tex = this.textureCache.get(key);
    if (!tex) return;
    this.textureCache.delete(key);
    // Pixi v8: Assets.unload(alias) drops the alias from the internal
    // resolver and decrements the texture's refcount. Errors swallowed
    // since unloading a never-loaded alias throws.
    try { void Assets.unload(key); } catch { /* swallow */ }
  }
}
